import type { Goal, Observation } from '@medplum/fhirtypes';

import {
  type GroupedObservations,
  type ObservationGroup,
} from '@eluve/fhir-observations';
import { formatGoalTarget } from '@eluve/fhir-renderer';
import { goalAchievement, isPrimaryGoal } from '@eluve/plan-shapes';

import { isPendingReview, isRetracted } from './review-markers';

/**
 * Page-side derivation of a patient's Goals into the buckets the card
 * renders (ENG-5367 scope). Pure derivations over the fetched set — no
 * fetching, no React. The renderer owns single-resource presentation; this
 * module owns list membership, ordering, and the goal ↔ tracked-observation
 * linkage (the walker renders one view per resource, so bucketing is
 * deliberately not a registry view).
 */

export type GoalList = {
  /** AI drafts awaiting review, newest first. */
  pending: Goal[];
  /** Goals being worked toward, primary first, then newest first. */
  inProgress: Goal[];
  /** Achieved / completed / cancelled goals, newest first. */
  ended: Goal[];
};

/** Stable routing key for the drawer deep link — the resource id. Unlike
 *  observations (which group many resources per measure), one goal row is
 *  one resource, so the id is the natural unique token. */
export function goalToken(goal: Goal): string {
  return goal.id ?? 'unknown';
}

/** The sortable clinical time of a goal (start, then last status change). */
export function goalSortKey(goal: Goal): string {
  return goal.startDate ?? goal.statusDate ?? '';
}

/** Achievement codes that mean the goal's outcome was reached and holds. */
const DONE_ACHIEVEMENTS = new Set(['achieved', 'sustaining']);
/** Lifecycle statuses that mean work on the goal is over. */
const ENDED_LIFECYCLES = new Set(['completed', 'cancelled', 'rejected']);

/**
 * Bucket a patient's goals for the card. Retracted (`entered-in-error`)
 * goals are dropped entirely — they are dismissed drafts or superseded
 * runs, not clinical history.
 */
export function buildGoalList(goals: readonly Goal[]): GoalList {
  const newestFirst = (a: Goal, b: Goal) =>
    goalSortKey(b).localeCompare(goalSortKey(a));

  const pending: Goal[] = [];
  const inProgress: Goal[] = [];
  const ended: Goal[] = [];

  for (const goal of goals) {
    if (isRetracted(goal)) continue;
    if (isPendingReview(goal)) {
      pending.push(goal);
      continue;
    }
    const achievement = goalAchievement(goal);
    if (
      ENDED_LIFECYCLES.has(goal.lifecycleStatus) ||
      (achievement && DONE_ACHIEVEMENTS.has(achievement))
    ) {
      ended.push(goal);
    } else {
      inProgress.push(goal);
    }
  }

  return {
    pending: pending.sort(newestFirst),
    // The plan's PRIMARY goal leads; ties within each group stay newest first.
    inProgress: inProgress.sort(
      (a, b) =>
        Number(isPrimaryGoal(b)) - Number(isPrimaryGoal(a)) ||
        newestFirst(a, b),
    ),
    ended: ended.sort(newestFirst),
  };
}

/** Total goals the card shows (the "N tracked" count). */
export function trackedGoalCount(list: GoalList): number {
  return list.pending.length + list.inProgress.length + list.ended.length;
}

/** Total drafts awaiting review. */
export function pendingReviewCount(list: GoalList): number {
  return list.pending.length;
}

/** The first draft to review, for the "N to review" pill. */
export function firstPendingGoal(list: GoalList): Goal | undefined {
  return list.pending[0];
}

/** Find a goal by its routing token across every bucket. */
export function findGoalByToken(
  list: GoalList,
  token: string | null,
): Goal | undefined {
  if (!token) return undefined;
  return [...list.pending, ...list.inProgress, ...list.ended].find(
    (goal) => goalToken(goal) === token,
  );
}

/**
 * The observation series a goal tracks, when it `addresses` an Observation
 * that is part of the loaded set: the addressed resource's whole measure
 * group (same code), so the row can draw the series sparkline — not just
 * the single addressed reading.
 */
export function trackedObservationGroup(
  goal: Goal,
  grouped: GroupedObservations | null,
): ObservationGroup | undefined {
  if (!grouped) return undefined;
  const addressedIds = (goal.addresses ?? [])
    .map((ref) => ref.reference)
    .filter((ref): ref is string => Boolean(ref?.startsWith('Observation/')))
    .map((ref) => ref.split('/')[1]);
  if (addressedIds.length === 0) return undefined;
  const groups = [...grouped.measures, ...grouped.others];
  for (const id of addressedIds) {
    const group = groups.find((candidate) =>
      candidate.observations.some((observation) => observation.id === id),
    );
    if (group) return group;
  }
  return undefined;
}

/** "Tracking …" label: the goal's own target measure, else the group's. */
export function trackingLabel(
  goal: Goal,
  group: ObservationGroup | undefined,
): string | undefined {
  return goal.target?.[0]?.measure?.text ?? group?.label;
}

/**
 * Display text for a goal's measurable target — moved to the renderer so
 * GoalReadView formats targets identically to the card and drawer;
 * re-exported here to keep this module the goal-list derivation surface.
 */
export { formatGoalTarget };

/** The latest validated reading of the tracked series (the "now" value). */
export function latestTrackedValue(
  group: ObservationGroup | undefined,
): { value: number; observation: Observation } | undefined {
  const last = group?.series[group.series.length - 1];
  return last
    ? { value: last.value, observation: last.observation }
    : undefined;
}
